import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCircle, X as XIcon, WarningCircle, CreditCard, ChatCircle, CalendarCheck } from "@phosphor-icons/react";
import api from "@/lib/api";

const ICON_MAP = {
  booking_created: CalendarCheck,
  booking_confirmed: CheckCircle,
  booking_rejected: XIcon,
  booking_completed: CheckCircle,
  deposit_refunded: CreditCard,
  damage_reported: WarningCircle,
  deposit_disputed: WarningCircle,
  payment_received: CreditCard,
  message_received: ChatCircle,
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const ref = useRef(null);
  const prevCountRef = useRef(0);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const [{ data: notifs }, { data: countData }] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setNotifications(notifs);
      const newCount = countData.count || 0;

      // Browser notification for new items
      if (permissionGranted && newCount > prevCountRef.current && prevCountRef.current > 0) {
        const latest = notifs.find(n => !n.read);
        if (latest && "Notification" in window) {
          new Notification("Borrow", { body: latest.title, icon: "/favicon.ico" });
        }
      }
      prevCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch {}
  }, [permissionGranted]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then(p => setPermissionGranted(p === "granted"));
    } else if ("Notification" in window) {
      setPermissionGranted(Notification.permission === "granted");
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      try {
        await api.put(`/notifications/${notif.id}/read`);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {}
    }
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-2 text-[#5C665D] hover:text-[#2C5545] transition-colors relative"
        data-testid="notification-bell"
      >
        <Bell size={20} weight={unreadCount > 0 ? "fill" : "duotone"} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-[#D96C4A] text-white rounded-full text-[9px] flex items-center justify-center font-semibold min-w-[18px] px-1" data-testid="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-[#EAE7E0] rounded-2xl shadow-xl overflow-hidden z-50 animate-fade-in" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#EAE7E0]">
            <h3 className="text-sm font-semibold text-[#1A1C1A]">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#2C5545] font-medium hover:underline" data-testid="mark-all-read-btn">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell size={24} weight="duotone" className="text-[#EAE7E0] mx-auto mb-2" />
                <p className="text-xs text-[#5C665D]">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 15).map(notif => {
                const Icon = ICON_MAP[notif.type] || Bell;
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full text-left px-4 py-3 hover:bg-[#FDFBF7] transition-colors border-b border-[#EAE7E0]/50 last:border-b-0 ${!notif.read ? "bg-[#2C5545]/[0.03]" : ""}`}
                    data-testid={`notification-${notif.id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${!notif.read ? "bg-[#D96C4A]/10" : "bg-[#EAE7E0]/50"}`}>
                        <Icon size={14} weight="duotone" className={!notif.read ? "text-[#D96C4A]" : "text-[#5C665D]"} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs leading-tight ${!notif.read ? "font-semibold text-[#1A1C1A]" : "text-[#5C665D]"}`}>{notif.title}</p>
                        <p className="text-[11px] text-[#5C665D] mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-[#5C665D]/60 mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                      {!notif.read && <div className="w-2 h-2 bg-[#D96C4A] rounded-full flex-shrink-0 mt-1.5" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
